import * as React from 'react';
import { Link } from 'react-router-dom';

import styled from '@emotion/styled';

import { Box, COLORS, SkeletonLoader } from '@clutter/clean';

import { ConditionNoteBadge, ItemCard } from '@portal/components/helpers/inventory/item_card';
import { SMALL_WIDTH, LARGE_WIDTH } from '@portal/components/helpers/inventory/layout';
import { isItemGroup } from '@portal/components/items/utilities/customer_item';
import { itemURL } from '@portal/config/routes';
import { CustomerItemFragment } from '@portal/schema';

import { useItemViewContext } from '../item_view_context';

const ReadOnlyCard = styled(ItemCard.Container)`
  pointer-events: none;
  cursor: default;
`;

const Loader = () => (
  <ItemCard.Container>
    <SkeletonLoader width="100%" height={[SMALL_WIDTH, null, null, LARGE_WIDTH]} />
    <Box padding="8px 24px" border={`1px solid ${COLORS.grayBackground}`} borderStyle="none none solid">
      <SkeletonLoader height="16px" />
    </Box>
  </ItemCard.Container>
);

const Item: React.FC<{ item: CustomerItemFragment; readOnly?: boolean }> = ({ item, readOnly = false }) => {
  const { setSelectedItem, selectedItem } = useItemViewContext();
  const selected = selectedItem?.uuid === item.uuid;

  if (readOnly) {
    return (
      <ReadOnlyCard data-test-id={`item-${item.uuid}`} aria-disabled>
        <ItemCard.Content
          item={item}
          selected={false}
          showPrimaryAction={false}
          showConditionNote={false}
          greyedPhotos
        />
      </ReadOnlyCard>
    );
  }

  return (
    <ItemCard.Container data-test-id={`item-${item.uuid}`}>
      <Link
        to={itemURL(item.uuid)}
        onClick={(e) => {
          // This link is technically valid, but wouldn't have the same filters.
          e.preventDefault();
          setSelectedItem(
            isItemGroup(item) ? { __typename: 'ItemGroup', uuid: item.uuid } : { __typename: 'Item', uuid: item.uuid },
          );
        }}
      >
        <ItemCard.Content
          item={item}
          selected={selected}
          selectedText="Viewing"
          unselectedText="View"
          showConditionNote={false}
        />
      </Link>
      <ConditionNoteBadge item={item} />
    </ItemCard.Container>
  );
};

const Combined = Object.assign(Item, { Loader });

export { Combined as ItemCard };
